// Screen 4 - cloud connect (slice-2-onboarding.md §7). Skippable - "Skip for now" is a
// real, always-focusable link, never disabled or hidden (escape-routes). Federation-first
// copy; the scoped-token fallback is a plain (unmasked) field, cleared once saved.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import type { Cloud } from "@nightcrew/shared";
import { FocusedFlowShell } from "../../components/FocusedFlowShell.js";
import { OuterStepper, SubStepper } from "../../components/Stepper.js";
import { setAppCloud } from "../../api/route.js";
import { useWizard } from "../../state/wizard.js";

const PROVIDERS: readonly {
  readonly value: Cloud;
  readonly label: string;
  readonly initials: string;
}[] = [
  { value: "aws", label: "AWS", initials: "AWS" },
  { value: "azure", label: "Azure", initials: "AZ" },
  { value: "gcp", label: "GCP", initials: "GCP" },
  { value: "scaleway", label: "Scaleway", initials: "SC" },
  { value: "ovh", label: "OVH", initials: "OV" },
  { value: "supabase", label: "Supabase", initials: "SB" },
  { value: "vercel", label: "Vercel", initials: "VC" },
];

export const CloudConnect = (): React.JSX.Element => {
  const navigate = useNavigate();
  const { draft } = useWizard();
  const [active, setActive] = useState<Cloud | null>(null);
  const [region, setRegion] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState<Cloud | null>(null);

  const goNext = (): void => {
    void navigate("/apps/new/route");
  };

  const handleConnect = async (): Promise<void> => {
    if (active === null || draft.appId === null || region.trim().length === 0) return;
    setSaving(true);
    setError(null);
    const result = await setAppCloud(draft.appId, { cloud: active, region: region.trim() });
    setSaving(false);
    if (isErr(result)) {
      setError(`We could not connect ${active}. Try again, or connect a different cloud.`);
      return;
    }
    setConnected(active);
  };

  return (
    <FocusedFlowShell
      title="Connect your cloud"
      onBack={() => {
        void navigate("/apps/new/connect");
      }}
      cta={
        <button type="button" className="btn btn-primary" onClick={goNext}>
          {connected !== null ? "Continue" : "Skip for now"}
        </button>
      }
    >
      <OuterStepper active="connect" />
      <SubStepper
        states={{ repo: "done", intent: "done", cloud: connected !== null ? "done" : "pending" }}
      />

      {connected !== null ? (
        <div className="card pad">
          <p>{connected} connected.</p>
        </div>
      ) : (
        <>
          <div className="provider-grid mt16">
            {PROVIDERS.map((provider) => (
              <button
                key={provider.value}
                type="button"
                className="provider-tile"
                aria-pressed={active === provider.value}
                onClick={() => {
                  setActive(provider.value);
                }}
              >
                <span className="logo" aria-hidden="true">
                  {provider.initials}
                </span>
                {provider.label}
              </button>
            ))}
          </div>

          {active !== null ? (
            <div className="card pad mt16" aria-expanded="true">
              <p className="muted" style={{ fontSize: 13 }}>
                Connect without a stored key. Nightcrew asks your cloud for a short-lived role,
                seconds at a time - nothing is ever stored on our side.
              </p>
              <div className="mt12">
                <label className="field-label" htmlFor="cloud-region">
                  Region
                </label>
                <input
                  id="cloud-region"
                  className="field"
                  value={region}
                  onChange={(event) => {
                    setRegion(event.target.value);
                  }}
                  placeholder="e.g. eu-west-1"
                />
              </div>
              {error !== null ? (
                <p className="field-error" role="alert">
                  {error}
                </p>
              ) : null}
              <button
                type="button"
                className="btn btn-primary btn-sm mt12"
                disabled={saving || region.trim().length === 0}
                onClick={() => {
                  void handleConnect();
                }}
              >
                {saving ? "Connecting..." : "Connect"}
              </button>
            </div>
          ) : null}
        </>
      )}

      <p className="muted mt16" style={{ fontSize: 12.5 }}>
        No cloud connected yet. You can add one anytime before you deploy.
      </p>
    </FocusedFlowShell>
  );
};
